import React, { createContext, useContext, useState, useCallback } from 'react';
import { sendRequest } from '../api';

const OplaesContext = createContext();

export const useOplaes = () => {
  const context = useContext(OplaesContext);
  if (!context) {
    throw new Error('useOplaes must be used within an OplaesProvider');
  }
  return context;
};

export const OplaesProvider = ({ children }) => {
  const [oplaes, setOplaes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOplaes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sendRequest('/oplaes', 'GET');
      setOplaes(data);
      return data;
    } catch (error) {
      setError(error.message || 'Failed to fetch oplaes');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createOplae = useCallback(async ({ 
    name, 
    color, 
    strategy, 
    goal,
    tasks = []
  }) => {
    try {
      setLoading(true);
      const data = await sendRequest('/oplaes', 'POST', {
        oplae: {
          name,
          color,
          strategy,
          goal,
          tasks
        }
      });
      
      // Update local state
      setOplaes(prev => [...prev, data]);

      return data;
    } catch (error) {
      setError(error.message || 'Failed to create oplae');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOplae = useCallback(async (id, { 
    name, 
    color, 
    strategy, 
    goal,
    tasks
  }) => {
    try {
      setLoading(true);
      const data = await sendRequest(`/oplaes/${id}`, 'PUT', {
        oplae: {
          name,
          color,
          strategy,
          goal,
          tasks
        }
      });

      // Update local state
      setOplaes(prev => prev.map(o => o.id === id ? data : o));

      return data;
    } catch (error) {
      setError(error.message || 'Failed to update oplae');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const destroyOplae = useCallback(async (id) => {
    try {
      setLoading(true);
      await sendRequest(`/oplaes/${id}`, 'DELETE');

      // Remove from state
      setOplaes(prev => prev.filter(o => o.id !== id));

      return true;
    } catch (error) {
      setError(error.message || 'Failed to delete oplae');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    oplaes,
    loading,
    error,
    fetchOplaes,
    createOplae,
    updateOplae,
    destroyOplae,
    clearError
  };

  return (
    <OplaesContext.Provider value={value}>
      {children}
    </OplaesContext.Provider>
  );
};